import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { Box } from "../../components/Layout";
import { PageTitle } from "../../components/PageTitle";
import { Skeleton } from "../../components/Skeleton";
import { useToast } from "../../components/Toast";
import CheckIcon from "../../icons/Check";
import { GlobeIcon } from "../../icons/Globe";
import { errorCodeOf, getLink, ShortLink } from "../../services/links";
import { OpenInNewIcon } from "../Sites/components/CardSite";
import { CopyIcon, formatDate, siteOf } from "./components/LinkCard";
import { Badge, CopyButton, Favicon } from "./components/LinkCard/styles";
import { LinkStats } from "./components/LinkStats";
import { qrPng, qrSvg } from "./qrCode";
import { BackLink, CreateButton, Note, ViewGrid, ViewHeader, ViewPanel, ViewSkeleton, ViewStack } from "./styles";

const prefix = "pages.links.view";

/** The short link as people read it: kpo.bio/black-friday. */
const shortText = (url: string) => url.replace(/^https?:\/\//, "");

// An arrow down onto a line: "download"
const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M8 2.5v7.5M4.75 6.75 8 10l3.25-3.25M3 13h10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Saves a file made here (the QR code) with a name. */
const download = (href: string, name: string) => {
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};

/**
 * The QR code of the short link: made here on arrival (no request, nothing to wait for), saved as PNG (to print) or
 * SVG (to design with).
 */
const QrCodePanel = ({ link }: { link: ShortLink }) => {
  const { t } = useTranslation();
  const [svg, setSvg] = useState<string>();
  const [hasError, setError] = useState(false);
  const fileName = `kpo-bio-${link.slug}`;

  useEffect(() => {
    let current = true;
    qrSvg(link.shortUrl)
      .then(made => current && setSvg(made))
      .catch(() => current && setError(true));
    return () => {
      current = false;
    };
  }, [link.shortUrl]);

  const savePng = async () => {
    try {
      if (!svg) return;
      // the same code as on screen (with the K), 1024 px
      download(await qrPng(svg), `${fileName}.png`);
    } catch {
      setError(true);
    }
  };

  const saveSvg = () => {
    if (!svg) return;
    const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
    download(url, `${fileName}.svg`);
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <ViewPanel aria-labelledby="link-qr-title">
      <h2 id="link-qr-title">{t(`${prefix}.qr.title`)}</h2>
      <p className="hint">{t(`${prefix}.qr.description`)}</p>
      <div className="qrBody">
        {/* the room of the code while it is made, so nothing moves */}
        {svg ? (
          <div
            className="qr"
            role="img"
            aria-label={t(`${prefix}.qr.alt`, { url: shortText(link.shortUrl) })}
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        ) : (
          <div className="qr" aria-hidden="true" />
        )}
        <div className="qrSide">
          <div className="qrActions">
            <CreateButton
              as="button"
              type="button"
              aria-label={t(`${prefix}.qr.png`)}
              disabled={!svg}
              onClick={savePng}>
              <DownloadIcon />
              PNG
            </CreateButton>
            <CreateButton
              as="button"
              type="button"
              className="secondary"
              aria-label={t(`${prefix}.qr.svg`)}
              disabled={!svg}
              onClick={saveSvg}>
              <DownloadIcon />
              SVG
            </CreateButton>
          </div>
        </div>
      </div>
      {hasError && <Note role="alert">{t(`${prefix}.qr.error`)}</Note>}
    </ViewPanel>
  );
};

/**
 * A Keepo Link: its title and site, the short link (copied with a tap), where it goes, when it was made, its tags, its
 * QR code, and (under it) its analytics.
 */
const LinkViewPage = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { id = "" } = useParams();
  const [link, setLink] = useState<ShortLink>();
  const [loadState, setLoadState] = useState<"loading" | "ready" | "notFound" | "error">("loading");
  const [iconFailed, setIconFailed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let current = true;
    setLoadState("loading");
    getLink(id)
      .then(found => {
        if (!current) return;
        setLink(found);
        setLoadState("ready");
      })
      .catch(error => current && setLoadState(errorCodeOf(error) === "NOT_FOUND" ? "notFound" : "error"));
    return () => {
      current = false;
    };
  }, [id]);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2500);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const back = (
    <BackLink to="/links">
      <span aria-hidden="true">← </span>
      {t(`${prefix}.back`)}
    </BackLink>
  );

  // the shape of the whole screen while the link loads: back, its icon, title and "Editar", the two panels, the analytics
  if (loadState === "loading") {
    return (
      <Box>
        <ViewSkeleton role="status" aria-label={t(`${prefix}.loading`)}>
          <Skeleton className="back" />
          <span className="header">
            <Skeleton className="icon" />
            <Skeleton className="title" />
            <Skeleton className="edit" />
          </span>
          <span className="panels">
            <Skeleton className="panel" />
            <Skeleton className="panel" />
          </span>
          <Skeleton className="stats" />
        </ViewSkeleton>
      </Box>
    );
  }

  if (!link || loadState !== "ready") {
    return (
      <Box>
        <ViewStack>
          <Note role="alert">{t(`${prefix}.${loadState === "notFound" ? "notFound" : "loadError"}`)}</Note>
          <Link to="/links">{t(`${prefix}.backToLinks`)}</Link>
        </ViewStack>
      </Box>
    );
  }

  const title = link.title || siteOf(link.destination);

  return (
    <Box>
      <ViewStack>
        {back}
        <ViewHeader>
          <Favicon aria-hidden="true">
            {link.favicon && !iconFailed ? (
              <img src={link.favicon} alt="" onError={() => setIconFailed(true)} />
            ) : (
              <GlobeIcon />
            )}
          </Favicon>
          <PageTitle title={title}>{title}</PageTitle>
          {link.status === "paused" && <Badge>{t("pages.links.paused")}</Badge>}
          {link.safety === "unsafe" && (
            <Badge className="danger" title={t("pages.links.unsafeHint")}>
              {t("pages.links.unsafe")}
            </Badge>
          )}
          <CreateButton as={Link} to={`/links/${link.id}/edit`} className="secondary edit">
            {t(`${prefix}.edit`)}
          </CreateButton>
        </ViewHeader>

        <ViewGrid>
          <ViewPanel aria-labelledby="link-info-title">
            <h2 id="link-info-title">{t(`${prefix}.shortLink`)}</h2>
            <div className="short">
              <span className="url" title={shortText(link.shortUrl)}>
                {shortText(link.shortUrl)}
              </span>
              <CopyButton
                type="button"
                className="pill"
                aria-label={t(`${prefix}.copy`)}
                onClick={() =>
                  navigator.clipboard
                    .writeText(link.shortUrl)
                    .then(() => setCopied(true))
                    .catch(() => {
                      setCopied(false);
                      toast.error(t("pages.links.copyError"), { description: t("pages.links.tryAgain") });
                    })
                }>
                {copied ? (
                  <>
                    <CheckIcon width={12} height={12} color="currentColor" aria-hidden="true" />
                    {t(`${prefix}.copied`)}
                  </>
                ) : (
                  <>
                    <CopyIcon />
                    {t(`${prefix}.copyShort`)}
                  </>
                )}
              </CopyButton>
              {/* the button's name hides its visible text: the copy is announced here */}
              <span className="sr-only" aria-live="polite">
                {copied ? t(`${prefix}.copied`) : ""}
              </span>
            </div>
            {/* each fact with its name above it, as the panel's title */}
            <dl>
              <div className="fact wide">
                <dt>{t(`${prefix}.destination`)}</dt>
                <dd>
                  {/* a link only for a web address (the API checks, this is the second lock) */}
                  {/^https?:\/\//i.test(link.destination) ? (
                    <a href={link.destination} target="_blank" rel="noopener noreferrer" title={link.destination}>
                      <span className="text">{link.destination}</span>
                      <OpenInNewIcon color="currentColor" />
                    </a>
                  ) : (
                    <span className="text">{link.destination}</span>
                  )}
                </dd>
              </div>
              {link.createdAt && (
                <div className="fact">
                  <dt>{t(`${prefix}.createdAt`)}</dt>
                  <dd>{formatDate(link.createdAt, i18n.language)}</dd>
                </div>
              )}
              {link.tags.length > 0 && (
                <div className="fact">
                  <dt>{t(`${prefix}.tags`)}</dt>
                  <dd className="tags">
                    {link.tags.map(tag => (
                      <span key={tag} className="tag">
                        {tag}
                      </span>
                    ))}
                  </dd>
                </div>
              )}
            </dl>
          </ViewPanel>

          <QrCodePanel link={link} />
        </ViewGrid>

        <LinkStats linkId={link.id} />
      </ViewStack>
    </Box>
  );
};

export default LinkViewPage;
